import data from "../../shared/sharedData";

export default {
  name: "Appendix D - Job Contractor and Other Joint Employer Information Instructions",
  pageInfo: {
    text: `
    <b>Job Contractors</b>: Pursuant to 20 CFR 655.19(d)(1), a job contractor that is filing as a joint employer with its employer-client must submit a completed 
    H-2B Application for Temporary Employment Certification, Form ETA-9142B, that clearly identifies the joint employers (the job contractor 
    and its employer-client) and the employment relationship (including the actual worksite(s) disclosed on the Form ETA-9142B).  
    A job contractor means a person, association, firm, or a corporation that meets the definition of an employer and that contracts 
    services or labor on a temporary basis to one or more employers, which is not an affiliate, branch, or subsidiary of the job contractor 
    and where the job contractor will not exercise substantial, direct day-to-day supervision and control in the performance of the services 
    or labor to be performed other than hiring, paying, and firing the workers.  20 CFR 655.5.  Pursuant to 20 CFR 655.19(a), a job contractor 
    may only submit an H-2B Application for Temporary Employment Certification, Form ETA-9142B, if it is filing as a joint employer with its 
    employer-client.  An employer-client means an employer that has entered into an agreement with a job contractor, as defined in 20 CFR 655.5.    
    <br></br><b>Important Note:</b> Employers are required to complete Appendix D when supplying information about the employer-client and its point of contact. 
    Submission of this information in any other form or format will not be accepted and will result in the application being rejected for processing by the Department.
    <br></br><b>Other Joint Employers</b>:  Separate from job contractors, the employer filing the Form ETA-9142B must disclose joint employers associated with the Form ETA-9142B filing.  
    To do so, the employer filing the Form ETA-9142B must complete Appendix D for each joint employer associated with the application, even if the joint employer is not a job contractor.  
    <b>Special Instructions for Joint Employers</b> - For “Section A:  Employer-Client Information”, enter the joint employer’s information.  For “Section B:  Employer-Client Information”, enter 
    the joint employer’s point of contact information.  Each joint employer must also complete an Appendix B form for the 9142B filing. `,
    coloring: `grey`,
    isVisible: {
      when: "empType",
      is: ["Joint Employer (e.g., Job Contractor)"],
      operator: "compareMultiFieldValuesAND_ORInArray",
    },
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify in Field G.1 whether you are filing as an Individual Employer or a Joint Employer.",
        type: "info",
        isVisible: {
          when: "empType",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you indicated that you are filing as an Individual Employer (per Field G.1), no Appendix D is necessary for this application.",
        type: "success",
        isVisible: {
          when: "empType",
          is: "Individual Employer",
        },
      },
    ],
  },
  sections: [
    {
      name: "Employer-Client or Other Joint Employer Name(s)",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        {
          machine_name: "apdxdLegalName",
          widget: "text",
          code: "A.1",
          label: "Legal Business Name",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full Employer Name.",
            },
          ],
          maxLength: 35,
          tooltip: `Enter the full name of the individual employer-client. The employer-client’s or joint employer’s full legal business name 
            is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the IRS.`,
        },
        {
          machine_name: "apdxdTradeName",
          code: "A.2",
          widget: "text",
          label: "Trade name/Doing Business As (DBA), if applicable",
          maxLength: 66,
          tooltip:
            "Enter the full trade name or “Doing Business As” (DBA) name of the employer-client or joint employer, if applicable. Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Employer-Client or Other Joint Employer Address",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        {
          machine_name: "apdxdEmpAddr1",
          widget: "text",
          code: "A.3",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
            "noNAs",
          ],
          maxLength: 60,
          tooltip: `Enter the street address of the employer-client’s or joint employer’s principal place of business. 
            The address must be a physical location and not a P.O. Box.`,
        },
        {
          machine_name: "apdxdEmpAddr2",
          code: "A.4",
          widget: "text",
          label: `Address 2 <i>(apartment/suite/floor and number)</i>`,
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this field to complete the employer-client’s 
          or joint employer’s street address. If no additional space is needed, enter “N/A.”`,
        },
        {
          code: "A.5",
          label: "Country",
          machine_name: "apdxdEmpCountry",
          widget: "select",
          selection: data.country,
          clearSchema: ["apdxdEmpState", "apdxdEmpPostcode"],
          validate: ["required"],
          tooltip: `Enter the country of the employer-client’s or joint employer’s principal place of business.`,
        },
        {
          machine_name: "apdxdEmpCity",
          code: "A.6",
          widget: "text",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          label: `City`,
          maxLength: 35,
          tooltip: `Enter the city of the employer-client’s or joint employer’s principal place of business.`,
        },
        {
          machine_name: "apdxdEmpState",
          code: "A.7",
          widget: "select",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "apdxdEmpCountry",
          isVisible: {
            when: "apdxdEmpCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "apdxdEmpCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `State`,
          maxLength: 35,
          tooltip: `Enter the State, District, or Territory of the employer-client’s or joint employer’s principal place of business.`,
        },
        {
          machine_name: "apdxdEmpPostcode",
          code: "A.8",
          widget: "text",
          disabled: true,
          disableWhenField: "apdxdEmpCountry",
          validate: [
            "required",
            {
              postalCountry: "apdxdEmpCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "apdxdEmpCountry",
              },
            },
            {
              isRequired: "apdxdEmpCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `Postal code`,
          maxLength: 10,
          tooltip: `Enter the postal (zip) code of the employer-client’s or joint employer’s principal place of business.`,
        },
        {
          code: "A.9",
          label: "Province",
          machine_name: "apdxdEmpProvince",
          widget: "text",
          disabled: true,
          disableWhenField: "apdxdEmpCountry",
          validate: [
            {
              isRequired: "apdxdEmpCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip: `Enter the province of the employer-client’s or joint employer’s principal place of business, 
          if applicable. Enter “N/A” if not applicable.`,
          maxLength: 25,
        },
      ],
    },
    {
      name: "Employer-Client or Other Joint Employer Contact Information",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        {
          code: "A.10",
          machine_name: "apdxdEmpPhone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip: `Enter the area code and telephone number for the employer-client’s or joint employer’s principal place of business. 
            Include country code, if outside of the United States.`,
        },
        {
          code: "A.11",
          machine_name: "apdxdEmpPhoneext",
          widget: "text",
          label: "Extension",
          maxLength: 4,
          validate: ["number"],
          parse: "positiveWholeNumber",
          tooltip: `Enter the extension of the telephone number for the employer-client’s or joint employer’s principal place of business, 
          if applicable. Enter “N/A” if not applicable.`,
        },
      ],
    },
    {
      name: "Employer-Client or Other Joint Employer Identifiers",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        {
          code: "A.12",
          machine_name: "apdxdEmpFein",
          widget: "text",
          label: "Federal Employer Identification Number (FEIN from IRS)",
          validate: [
            "required",
            "fein",
            { minLength: 10, message: "This is not a valid FEIN" },
            {
              FEINPrefixValidator: "apdxdEmpFein",
              needsFieldValues: true,
              message: "This is not a valid FEIN",
            },
          ],
          parse: "fein",
          maxLength: 10,
          tooltip: `Enter the nine-digit Federal Employer identification Number (FEIN) of the employer-client or joint employer 
          as assigned by the IRS. Do not enter a social security number. <br></br> <b>Note</b>: All employers, <u>including private 
          households</u>, MUST obtain an FEIN from the IRS before completing this application. Information on obtaining an 
          FEIN can be found at <a href="http://www.IRS.gov">www.IRS.gov</a>.`,
        },
        {
          code: "A.13",
          machine_name: "apdxdEmpNaics",
          field_to_modify: "apdxdEmpNaicsDescription",
          widget: "naics",
          label: "NAICS Code",
          validate: ["required"],
          tooltip: `Enter the four-digit NAICS code that best describes the employer’s business, not the H-2B job opportunity. 
          A listing of NAICS codes can be found at <a target="_blank" href="https://www.census.gov/naics/">www.census.gov/naics/</a>.`,
        },
      ],
    },
    {
      name: "Employer-Client or Other Joint Employer Point of Contact Name & Title",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        // {
        //   widget: "inlinePrefiller",
        //   prefillLabel: "Select an Employer Point of Contact profile to populate this section (optional)",
        //   code: "D",
        //   prefill: {
        //     schema: "employerPocs",
        //     type: "employer-pocs",
        //     formName: "9142B",
        //     formSection: "D",
        //   },
        // },
        {
          code: "B.1",
          machine_name: "apdxdPocLastname",
          widget: "text",
          label: "Contact's Last (family) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Contact's full Last Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the last (family) name of the employer-client’s or joint employer’s point of contact.",
        },
        {
          code: "B.2",
          machine_name: "apdxdPocFirstname",
          widget: "text",
          label: "First (given) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Contact's full First Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the first (given) name of the employer-client’s or joint employer’s point of contact.",
        },
        {
          code: "B.3",
          machine_name: "apdxdPocMiddlename",
          widget: "text",
          label: "Middle name(s)",
          maxLength: 35,
          tooltip:
            "Enter the middle name(s) of the employer-client’s or joint employer’s point of contact, if applicable. Enter “N/A” if not applicable.",
        },
        {
          code: "B.4",
          machine_name: "apdxdPocJobtitle",
          widget: "text",
          label: "Contact's Job Title",
          validate: [
            "required",
            {
              minLength: 5,
              message:
                "Contact's full Job Title must contain at least 5 characters.",
            },
          ],
          maxLength: 60,
          tooltip:
            "Enter the job title of the employer-client’s or joint employer’s point of contact.",
        },
      ],
    },
    {
      name: "Employer-Client or Other Joint Employer Point of Contact Address",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        {
          code: "B.5",
          machine_name: "apdxdPocAddr1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
          ],
          maxLength: 60,
          tooltip: `Enter the business street address for the employer-client’s or joint employer’s point of contact. 
          The address must be a physical location and not a P.O. Box.`,
        },
        {
          code: "B.6",
          machine_name: "apdxdPocAddr2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this field to complete the street address. 
          If no additional space is needed, enter “N/A.”`,
        },
        {
          code: "B.7",
          machine_name: "apdxdPocCountry",
          widget: "select",
          label: "Country",
          clearSchema: ["apdxdPocState", "apdxdPocPostcode"],
          selection: data.country,
          validate: ["required"],
          tooltip:
            "Enter the country of the employer-client’s or joint employer’s point of contact.",
        },
        {
          code: "B.8",
          machine_name: "apdxdPocCity",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the city of the employer-client’s or joint employer’s point of contact.",
        },
        {
          code: "B.9",
          machine_name: "apdxdPocState",
          widget: "select",
          label: "State",
          selection: data.state,
          state: true,
          isVisible: {
            when: "apdxdPocCountry",
            is: "UNITED STATES OF AMERICA",
          },
          validate: ["required"],
          tooltip:
            "Enter the State, District, or Territory of the employer-client’s or joint employer’s point of contact.",
        },
        {
          code: "B.10",
          machine_name: "apdxdPocPostcode",
          widget: "text",
          label: "Postal Code",
          validate: [
            "required",
            {
              postalCountry: "apdxdPocCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "apdxdPocCountry",
              },
            },
            {
              isRequired: "apdxdPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],

          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer-client’s or joint employer’s point of contact.",
        },
        {
          code: "B.11",
          machine_name: "apdxdPocProvince",
          widget: "text",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              isRequired: "apdxdPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip:
            "Enter the province of the employer-client’s or joint employer’s point of contact, if applicable. Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Employer-Client or Other Joint Employer Point of Contact Information",
      isVisible: {
        when: "empType",
        is: "Joint Employer (e.g., Job Contractor)",
      },
      fields: [
        {
          code: "B.12",
          machine_name: "apdxdPocPhone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip: `Enter the area code and business telephone number of the employer-client’s or joint employer’s point of contact. 
          Include country code, if the point of contact is located outside of the United States.`,
        },
        {
          code: "B.13",
          machine_name: "apdxdPocPhoneext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber", //don't allow decimal Number and negative number // not working for me? -NG
          maxLength: 4,
          tooltip: `Enter the extension of the telephone number of the employer-client’s or joint employer’s point of contact, if applicable. 
            Enter “N/A” if not applicable.`,
        },
        {
          code: "B.14",
          machine_name: "apdxdPocEmail",
          widget: "text",
          label: "Business e-mail address",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
          ],
          maxLength: 100,
          tooltip: `Enter the business email address of the employer-client’s or joint employer’s point of contact in the format name@emailaddress.top-level domain. 
          The email entered in this field must be the same as the one regularly used by the employer-client’s point of contact for its business 
          operations and capable of sending and receiving electronic communications from the Department with respect to the processing of 
          this application. If the employer-client’s or joint employer’s point of contact does not possess a business email address, please enter “N/A.”`,
        },
      ],
    },
  ],
};
